import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Camera, CalendarDays, HeartPulse, ImageOff, Plus, X } from 'lucide-react';
import { ApiError, fetchObjectUrl } from '@/api/client';
import { assetHealthApi, type AssetHealthCheck } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/design-system/Button';
import { StatusBadge } from '@/design-system/DataDisplay';
import { EmptyState, InlineError, Skeleton } from '@/design-system/Feedback';
import { Select, TextArea, TextInput } from '@/design-system/Inputs';
import { PermissionGate } from '@/design-system/Layout';
import { Dialog } from '@/design-system/Overlay';
import { downscaleImage } from '@/lib/downscaleImage';
import { formatDate, formatDateTime, humanise } from '@/lib/format';
import { checkPhotoFile, ACCEPTED_PHOTO_TYPES } from '../employees/PhotoPicker';

/**
 * An asset's health checks: dated inspections, each with its photographs.
 *
 * Shown as a timeline, newest inspection first, because the question people bring to it is "what
 * did this look like, and when" - a damage dispute is settled by the dated photo from before the
 * handover, not by the current condition field.
 */

const MAX_PHOTOS = 6;

const CONDITION_TONE = {
  NEW: 'success',
  GOOD: 'success',
  FAIR: 'warning',
  POOR: 'warning',
  DAMAGED: 'danger',
} as const;

export function HealthChecksTab({ assetId }: { assetId: string }) {
  const [recordOpen, setRecordOpen] = useState(false);
  const [viewing, setViewing] = useState<{ check: AssetHealthCheck; photoId: string } | null>(
    null,
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.assets.healthChecks(assetId),
    queryFn: () => assetHealthApi.list(assetId),
  });

  const checks = data?.content ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-small text-text-muted">
          Dated inspections of this device, newest first. Records cannot be edited - record a new
          check to correct one.
        </p>
        <PermissionGate permission={PERMISSIONS.ASSET_WRITE}>
          <Button
            variant="primary"
            onClick={() => setRecordOpen(true)}
            leadingIcon={<Plus className="size-4" />}
          >
            Record health check
          </Button>
        </PermissionGate>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : error ? (
        <InlineError error={error} onRetry={() => refetch()} />
      ) : checks.length === 0 ? (
        <div className="rounded-xl border border-border-subtle bg-surface">
          <EmptyState
            icon={<HeartPulse className="size-5" />}
            title="No health checks yet"
            description="Record a check with photos whenever the device is inspected - at handover, on return, or when damage is reported."
          />
        </div>
      ) : (
        <ol className="relative space-y-4 border-l border-border-default pl-6">
          {checks.map((check) => (
            <li key={check.id} className="relative">
              <span
                aria-hidden
                className="absolute top-4 -left-[1.95rem] grid size-4 place-items-center rounded-full border-2 border-surface bg-brand"
              />
              <article className="rounded-xl border border-border-subtle bg-surface p-4">
                <header className="flex flex-wrap items-center gap-2">
                  <CalendarDays aria-hidden className="size-4 text-text-muted" />
                  <h3 className="text-h3 text-text-primary">{formatDate(check.checkedOn)}</h3>
                  {check.condition && (
                    <StatusBadge tone={CONDITION_TONE[check.condition]}>
                      {humanise(check.condition)}
                    </StatusBadge>
                  )}
                  <span className="ml-auto text-caption text-text-muted">
                    Recorded by {check.recordedBy ?? 'unknown'} · {formatDateTime(check.recordedAt)}
                  </span>
                </header>

                {check.notes && (
                  <p className="mt-2 whitespace-pre-line text-small text-text-secondary">
                    {check.notes}
                  </p>
                )}

                {check.photos.length > 0 && (
                  <ul className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                    {check.photos.map((photo, index) => (
                      <li key={photo.id}>
                        <button
                          type="button"
                          onClick={() => setViewing({ check, photoId: photo.id })}
                          className="block aspect-square w-full overflow-hidden rounded-lg border border-border-subtle bg-surface-sunken transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                          aria-label={`Open photo ${index + 1} from ${formatDate(check.checkedOn)}`}
                        >
                          <ProtectedImage
                            path={assetHealthApi.photoPath(assetId, check.id, photo.id)}
                            className="size-full object-cover"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            </li>
          ))}
        </ol>
      )}

      <RecordHealthCheckDialog
        assetId={assetId}
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
      />

      <Dialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing ? `Health check · ${formatDate(viewing.check.checkedOn)}` : 'Photo'}
        description={
          viewing?.check.condition ? `Condition: ${humanise(viewing.check.condition)}` : undefined
        }
        size="xl"
      >
        {viewing && (
          <ProtectedImage
            path={assetHealthApi.photoPath(assetId, viewing.check.id, viewing.photoId)}
            className="max-h-[70vh] w-full rounded-lg object-contain"
          />
        )}
      </Dialog>
    </div>
  );
}

/**
 * An image from a protected endpoint.
 *
 * A bare `<img src>` is requested without the bearer token and comes back 401, so the bytes are
 * fetched through the authenticated client. The object URL is revoked on unmount; without that a
 * timeline of photos would stay in memory for the life of the tab.
 */
function ProtectedImage({ path, className }: { path: string; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    const controller = new AbortController();
    setFailed(false);
    fetchObjectUrl(path, controller.signal)
      .then((created) => {
        objectUrl = created;
        setUrl(created);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (failed) {
    return (
      <span className="grid size-full min-h-24 place-items-center text-text-muted">
        <ImageOff aria-hidden className="size-5" />
        <span className="sr-only">This photo could not be loaded</span>
      </span>
    );
  }
  if (!url) return <Skeleton className={className} />;
  return <img src={url} alt="" className={className} />;
}

interface FormValues {
  checkedOn: string;
  condition: string;
  notes: string;
}

export function todayIso() {
  // The local calendar day, not UTC: at 2am in India the UTC date is still yesterday.
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function RecordHealthCheckDialog({
  assetId,
  open,
  onClose,
}: {
  assetId: string;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [photos, setPhotos] = useState<File[]>([]);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { checkedOn: todayIso(), condition: '', notes: '' } });

  const record = useMutation({
    mutationFn: (values: FormValues) =>
      assetHealthApi.record(assetId, {
        checkedOn: values.checkedOn,
        condition: values.condition || undefined,
        notes: values.notes.trim() || undefined,
        photos,
      }),
    onSuccess: (check) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.assets.healthChecks(assetId) });
      toast.success('Health check recorded', {
        description: `${formatDate(check.checkedOn)} · ${check.photos.length} photo${
          check.photos.length === 1 ? '' : 's'
        }`,
      });
      close(true);
    },
    onError: (error) => setSubmitError(error),
  });

  const close = (force = false) => {
    if (record.isPending && !force) return;
    reset({ checkedOn: todayIso(), condition: '', notes: '' });
    setPhotos([]);
    setSubmitError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() => close()}
      title="Record a health check"
      description="What the device looked like on the day it was inspected. Once saved it cannot be edited."
      size="lg"
      busy={record.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={() => close()} disabled={record.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={record.isPending}
            onClick={handleSubmit((values) => {
              if (photos.length === 0 && !values.condition && !values.notes.trim()) {
                setSubmitError(new Error('Add at least a photo, a condition or a note.'));
                return;
              }
              record.mutate(values);
            })}
          >
            Save health check
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(event) => event.preventDefault()} noValidate>
        {submitError ? (
          submitError instanceof ApiError ? (
            <InlineError error={submitError} />
          ) : (
            <p role="alert" className="text-small text-danger-text">
              {(submitError as Error).message}
            </p>
          )
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Checked on"
            type="date"
            required
            max={todayIso()}
            error={errors.checkedOn?.message}
            {...register('checkedOn', {
              required: 'Choose the inspection date',
              validate: (value) => value <= todayIso() || 'The date cannot be in the future',
            })}
          />
          <Select
            label="Condition found"
            options={[
              { value: '', label: 'Not assessed' },
              ...(['NEW', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'] as const).map((condition) => ({
                value: condition,
                label: humanise(condition),
              })),
            ]}
            {...register('condition')}
          />
        </div>

        <TextArea
          label="Notes"
          rows={3}
          maxLength={2000}
          placeholder="Scratches on the lid, battery swelling, missing keys…"
          {...register('notes')}
        />

        <HealthPhotosField photos={photos} onChange={setPhotos} disabled={record.isPending} />
      </form>
    </Dialog>
  );
}

/**
 * Choosing up to six health-check photos, with previews.
 *
 * Shared by the health-check dialog and asset registration, so both validate, resize and preview
 * photos the same way. Files are resized as they are added, so what the caller holds is already the
 * upload-ready version.
 */
export function HealthPhotosField({
  photos,
  onChange,
  disabled = false,
  description = 'JPEG, PNG or WebP. Large photos are resized before upload.',
}: {
  photos: File[];
  onChange: (photos: File[]) => void;
  disabled?: boolean;
  description?: string;
}) {
  const [previews, setPreviews] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Preview URLs are object URLs and must be revoked when the selection changes.
  useEffect(() => {
    const urls = photos.map((photo) => URL.createObjectURL(photo));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [photos]);

  const add = async (files: FileList | null) => {
    setError(null);
    if (!files?.length) return;
    const chosen = Array.from(files);

    const problem = chosen.map(checkPhotoFile).find(Boolean);
    if (problem) {
      setError(problem);
      return;
    }
    if (photos.length + chosen.length > MAX_PHOTOS) {
      setError(`At most ${MAX_PHOTOS} photos per check.`);
      return;
    }
    const resized = await Promise.all(chosen.map(downscaleImage));
    onChange([...photos, ...resized]);
  };

  const full = photos.length >= MAX_PHOTOS;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-small font-medium text-text-primary">
          Photos{' '}
          <span className="font-normal text-text-muted">
            ({photos.length}/{MAX_PHOTOS})
          </span>
        </span>
        <label
          className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-border-default bg-surface px-3 text-[0.8125rem] font-medium text-text-primary shadow-xs hover:bg-surface-hover ${
            full || disabled ? 'pointer-events-none opacity-50' : ''
          }`}
        >
          <Camera aria-hidden className="size-4" />
          Add photos
          <input
            type="file"
            multiple
            disabled={full || disabled}
            // `capture` is deliberately not set: it forces the camera on phones and hides the
            // gallery, and the photo was often taken earlier.
            accept={ACCEPTED_PHOTO_TYPES.join(',')}
            className="sr-only"
            onChange={(event) => {
              void add(event.target.files);
              event.target.value = '';
            }}
          />
        </label>
      </div>

      {photos.length > 0 && (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {previews.map((preview, index) => (
            <li key={preview} className="relative">
              <img
                src={preview}
                alt=""
                className="aspect-square w-full rounded-lg border border-border-subtle object-cover"
              />
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(photos.filter((_, position) => position !== index))}
                className="absolute top-1 right-1 grid size-6 place-items-center rounded-full bg-black/60 text-white hover:bg-black/80"
                aria-label={`Remove photo ${index + 1}`}
              >
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="text-caption text-text-muted">{description}</p>
      {error && (
        <p role="alert" className="text-caption text-danger-text">
          {error}
        </p>
      )}
    </div>
  );
}
